import { OPEN_MODAL } from 'redux/modules/common-modal/actions/types';

import { ModalCloseOptions } from 'constants/modalCloseOptions';
import { BOOK_SLOT, CHANGE_SLOT_DATE, CHANGE_SLOT_TYPE } from 'constants/restrictions';
import { getSlotTypeConflictUrl } from 'redux/modules/slot-booking/selectors/get-slot-type-conflict-url';

import { bookSlotQueryString } from 'utils/book-slot-query-string';
import splitToLines from 'utils/split-to-lines';
import { getFromBlendedItemsById } from 'redux/modules/trolley/selectors/get-optimistic-item';
import { trackModalOpened, trackNotAdded } from 'analytics/modal-tracking';
import extractLineNumberFromSku from 'utils/extract-linenumber-from-sku';
import { getCustomerId } from 'redux/modules/sessions/selectors';
import { changeToAGroceriesCollectionModal, modalSeverityValues } from 'constants/modals';
import { ButtonProps } from '@johnlewispartnership/wtr-ingredients/ingredients/Button';
import noop from 'lodash/noop';
import { push } from 'redux-first-history';
import { setCloseCallback } from '.';

export interface IConflictModal {
  id?: string;
  messages: { title: string; reason: string; dismissal: string; aside: string };
  itemId: string;
  productId: string;
  resolutionActions: {
    id: string;
    message: string;
  }[];
  slotOptionDates: unknown[];
}

export const openConflictModal =
  (conflict: IConflictModal): WtrThunkAction<void> =>
  (dispatch, getState) => {
    setCloseCallback(noop);

    const trackingValues = {
      id: conflict.id || 'conflictModal',
      severity: modalSeverityValues.CONFLICT,
      title: conflict.messages.title,
      customerId: getCustomerId(getState()),
    };

    const editSlotUrl = getSlotTypeConflictUrl(getState());

    if (conflict.messages.reason.includes('left in stock')) {
      const { itemId, productId } = conflict;
      const lineNumber = extractLineNumberFromSku(productId);
      const trolleyItem = getFromBlendedItemsById(getState(), itemId);
      const { quantity: { amount = undefined } = {} } = trolleyItem;
      trackNotAdded(amount, lineNumber);
    }

    trackModalOpened(trackingValues);

    const isChangeToAGroceriesCollectionRequired =
      conflict.resolutionActions &&
      conflict.resolutionActions.find(
        action =>
          action.id === 'CHANGE_SLOT_TYPE' &&
          action.message.includes('Change to a Groceries collection'),
      );

    if (isChangeToAGroceriesCollectionRequired) {
      dispatch({
        type: OPEN_MODAL,
        conflict,
        ...changeToAGroceriesCollectionModal,
      });
      setCloseCallback((closeValue: unknown) => {
        if (closeValue === ModalCloseOptions.CONFIRM) {
          dispatch(
            push(
              `${editSlotUrl}${bookSlotQueryString(editSlotUrl, conflict.slotOptionDates || [])}`,
            ),
          );
        }
      });
      return;
    }

    const buttons = [];

    if (conflict.messages.dismissal) {
      buttons.push({
        buttonText: conflict.messages.dismissal,
        buttonValue: ModalCloseOptions.OK,
        theme: 'primary' as ButtonProps['theme'],
      });
    }

    (conflict.resolutionActions || []).forEach(resolution => {
      if (resolution.id && resolution.message) {
        if (resolution.id === BOOK_SLOT || resolution.id === CHANGE_SLOT_DATE) {
          buttons.push({
            buttonText: resolution.message,
            theme: 'primary',
            to: `${editSlotUrl}${bookSlotQueryString(editSlotUrl, conflict.slotOptionDates || [])}`,
          });
        } else if (resolution.id === CHANGE_SLOT_TYPE) {
          buttons.push({
            buttonText: resolution.message,
            theme: 'primary',
            to: editSlotUrl,
          });
        }
      }
    });

    if (buttons.length > 1) {
      buttons[0].theme = 'secondary';
    }

    dispatch({
      type: OPEN_MODAL,
      asideText: conflict.messages.aside,
      buttons: buttons.length > 0 ? buttons : undefined, // Ensures the default button is used if buttons is empty
      cannotClose: true,
      conflict,
      id: trackingValues.id,
      messageText: splitToLines(conflict.messages.reason),
      titleText: trackingValues.title,
      severity: trackingValues.severity,
      useIngredientsModal: false,
    });
  };
